import { ActionTree } from 'vuex';
import {
  ICollectionState,
  IGetPostersPayload,
  IGetPosterPayload,
  IGetPrizesPayload,
  IGetMysteryBoxesPayload,
  IGetCollectionOveriewPayload,
  IGetMomentPacksPayload,
  IGetSceneStatsPayload,
  IGetScenesPayload,
  IGetCollectiblesPayload,
  IGetProjectSceneMomentsRewardDetailsPayload,
  IGetSceneAPIsPayload,
} from './types';
import { IRootState } from '../types';
import {
  IMysteryBox,
  IPoster,
  IPrize,
  IShard,
  IScene,
  IPack,
  ICollectible,
} from '~/types/collections';
import {
  IFilmCollectionOverview,
  IFilmItemCollection,
  IFilmMomentStatsOverview,
  IFilmSceneStatsOverview,
  ISceneStats,
  IUserPopcornBuckets,
  IUserRewardsDistribution,
} from '~/types/my-collection';

export const actions: ActionTree<ICollectionState, IRootState> = {
  async getCollectionOverview(
    { commit, dispatch, state },
    { slug }: IGetCollectionOveriewPayload,
  ) {
    const projectStats = await this.$filmApiService.getCollectionOverview(slug);
    await dispatch('updateProjectStats', { projectStats, projectSlug: slug });
  },

  async updateProjectStats(
    { commit, dispatch, state },
    payload: {
      projectStats: IFilmCollectionOverview;
      projectSlug: string;
    },
  ) {
    commit('updateProjectStats', {
      projectStats: payload.projectStats,
      projectSlug: payload.projectSlug,
    });
  },

  async getPosters({ commit, dispatch, state }, { slug }: IGetPostersPayload) {
    const posters: IPoster[] = await this.$filmApiService.getPosters(slug);
    await dispatch('updatePosters', { posters, projectSlug: slug });
  },

  async updatePosters(
    { commit, dispatch, state },
    payload: { posters: IPoster[]; projectSlug: string },
  ) {
    commit('updatePosters', {
      posters: payload.posters,
      projectSlug: payload.projectSlug,
    });
  },

  async getShards(
    { commit, dispatch, state },
    { slug, id }: IGetPosterPayload,
  ) {
    const shards: IShard[] = await this.$filmApiService.getShards(slug);
    await dispatch('updateShards', { shards, projectSlug: slug });
  },

  async updateShards(
    { commit, dispatch, state },
    payload: { shards: IShard[]; projectSlug: string },
  ) {
    commit('updateShards', {
      shards: payload.shards,
      projectSlug: payload.projectSlug,
    });
  },

  async getPrizes({ commit, dispatch, state }, { slug }: IGetPrizesPayload) {
    const prizes: IPrize[] = await this.$filmApiService.getPrizes(slug);
    await dispatch('updatePrizes', { prizes, projectSlug: slug });
  },

  async updatePrizes(
    { commit, dispatch, state },
    payload: { prizes: IPrize[]; projectSlug: string },
  ) {
    commit('updatePrizes', {
      prizes: payload.prizes,
      projectSlug: payload.projectSlug,
    });
  },

  async getMysteryBoxes(
    { commit, dispatch, state },
    { slug }: IGetMysteryBoxesPayload,
  ) {
    const mysteryBoxes: IMysteryBox[] =
      (await this.$filmApiService.getMysteryBoxes(slug)) || [];
    await dispatch('updateMysteryBoxes', { mysteryBoxes, projectSlug: slug });
  },

  async updateMysteryBoxes(
    { commit, dispatch, state },
    payload: { mysteryBoxes: IMysteryBox[]; projectSlug: string },
  ) {
    commit('updateMysteryBoxes', {
      mysteryBoxes: payload.mysteryBoxes,
      projectSlug: payload.projectSlug,
    });
  },

  async getScenes(
    { commit, dispatch, state },
    { projectSlug }: IGetScenesPayload,
  ) {
    const scenes: IScene[] =
      (await this.$filmApiService.getScenes(projectSlug)) || [];
    await dispatch('updateScenes', { scenes, projectSlug });
  },

  async updateScenes(
    { commit, dispatch, state },
    payload: { scenes: IScene[]; projectSlug: string },
  ) {
    commit('updateScenes', {
      scenes: payload.scenes,
      projectSlug: payload.projectSlug,
    });
  },

  async getMomentPacks(
    { commit, dispatch, state },
    { projectSlug }: IGetMomentPacksPayload,
  ) {
    const momentPacks: IPack[] =
      (await this.$filmApiService.getPacks(projectSlug)) || [];
    await dispatch('updateMomentPacks', { momentPacks, projectSlug });
  },

  async updateMomentPacks(
    { commit, dispatch, state },
    payload: { momentPacks: IPack[]; projectSlug: string },
  ) {
    commit('updateMomentPacks', {
      momentPacks: payload.momentPacks,
      projectSlug: payload.projectSlug,
    });
  },

  async getCollectibles(
    { commit, dispatch, state },
    { projectSlug }: IGetCollectiblesPayload,
  ) {
    const collectibles: IPack[] =
      (await this.$filmApiService.getCollectibles(projectSlug)) || [];
    await dispatch('updateCollectibles', { collectibles, projectSlug });
  },

  async updateCollectibles(
    { commit, dispatch, state },
    payload: { collectibles: ICollectible[]; projectSlug: string },
  ) {
    commit('updateCollectibles', {
      collectibles: payload.collectibles,
      projectSlug: payload.projectSlug,
    });
  },

  async getSceneStats(
    { commit, dispatch, state },
    { projectSlug, sceneSlug }: IGetSceneStatsPayload,
  ) {
    const sceneStats = await this.$filmApiService.getSceneStats(
      projectSlug,
      sceneSlug,
    );
    await dispatch('updateSceneStats', {
      sceneStats: {
        totalRewards: (Number(sceneStats.totalRewards) || 0)?.toFixed?.(2) || 0,
        lastDayRewards:
          (Number(sceneStats.lastDayRewards) || 0)?.toFixed?.(2) || 0,
        distroBoost: sceneStats.distroBoost,
      },
      projectSlug,
      sceneSlug,
    });
    for (const momentId of Object.keys(sceneStats?.momentRewards ?? {})) {
      await dispatch('updateMomentStats', {
        momentStats: sceneStats?.momentRewards?.[momentId],
        projectSlug,
        momentId,
      });
    }
  },

  async updateSceneStats(
    { commit, dispatch, state },
    payload: {
      sceneStats: ISceneStats;
      sceneSlug: string;
      projectSlug: string;
    },
  ) {
    const scenePayload = {
      sceneStats: {
        totalRewards:
          (Number(payload?.sceneStats?.totalRewards) || 0)?.toFixed?.(2) || 0,
        lastDayRewards:
          (Number(payload?.sceneStats?.lastDayRewards) || 0)?.toFixed?.(2) || 0,
        distroBoost: payload?.sceneStats?.distroBoost || 0,
      },
      projectSlug: payload.projectSlug,
      sceneSlug: payload.sceneSlug,
    };
    commit('updateSceneStats', scenePayload);
  },

  async updateMomentStats(
    { commit, dispatch, state },
    payload: {
      momentStats: IFilmMomentStatsOverview;
      momentId: string;
      projectSlug: string;
    },
  ) {
    commit('updateMomentStats', {
      momentStats: payload?.momentStats,
      projectSlug: payload.projectSlug,
      momentId: payload.momentId,
    });
  },

  async getCollections({ commit, dispatch, state }, payload) {
    const collections = await this.$filmApiService.getCollections();
    const { projectCollections, miscCollection, filmItems } = collections ?? {};
    console.log(
      `[action][getCollections]`,
      projectCollections,
      miscCollection,
      filmItems,
    );

    const miscItems = miscCollection?.items ?? [];
    commit('updateMiscItems', { items: miscItems });
    commit('updateFilmItems', { items: filmItems });

    const entries: Array<[string, IFilmItemCollection]> = Object.entries(
      projectCollections ?? {},
    );
    for (const [projectSlug, projectCollection] of entries) {
      await dispatch('updateProjectStats', {
        projectStats: projectCollection.filmRewardMetrics,
        projectSlug,
      });
      commit('updateProjectItems', {
        items: projectCollection.items,
        projectSlug,
      });
    }
  },

  async initiateCollectionPage(
    { commit, dispatch, state },
    { projectSlug }: IGetProjectSceneMomentsRewardDetailsPayload,
  ) {
    const prms: Array<Promise<any>> = [
      this.$filmApiService.getCollectionOverview(projectSlug), // `${this.collectionsUrl}/${projectSlug}/collection-overview`,
      this.$filmApiService.getPosters(projectSlug),
      this.$filmApiService.getScenes(projectSlug),
      this.$filmApiService.getShards(projectSlug),
      this.$filmApiService.getPrizes(projectSlug),
      this.$filmApiService.getCollectibles(projectSlug),
      this.$filmApiService.getMysteryBoxes(projectSlug),
      this.$filmApiService.getPacks(projectSlug),
      this.$filmApiService.getProjectScenesRewardDetails(projectSlug), // `${this.collectionsUrl}/project/${projectSlug}/scenes/reward-details`,
    ];

    await Promise.all(prms)
      .then(async (responses) => {
        const [
          projectStats,
          posters,
          scenes,
          shards,
          prizes,
          collectibles,
          mysteryBoxes,
          momentPacks,
          projectScenesRewards,
        ] = responses;

        await dispatch('updateProjectStats', { projectStats, projectSlug }); // gets project rewards
        await dispatch('updatePosters', { posters, projectSlug });
        await dispatch('updateScenes', { scenes, projectSlug });
        await dispatch('updateShards', { shards, projectSlug });
        await dispatch('updatePrizes', { prizes, projectSlug });
        await dispatch('updateCollectibles', { collectibles, projectSlug });
        await dispatch('updateMysteryBoxes', { mysteryBoxes, projectSlug });
        await dispatch('updateMomentPacks', { momentPacks, projectSlug });

        for (const sceneSlug of Object.keys(projectScenesRewards)) {
          const sceneStats = projectScenesRewards[sceneSlug];
          await dispatch('updateSceneStats', {
            sceneStats: {
              totalRewards:
                (Number(sceneStats.totalRewards) || 0)?.toFixed?.(2) || 0,
              lastDayRewards:
                (Number(sceneStats.lastDayRewards) || 0)?.toFixed?.(2) || 0,
              distroBoost: sceneStats.distroBoost,
            },
            projectSlug,
            sceneSlug,
          });
          for (const momentId of Object.keys(sceneStats?.momentRewards || {})) {
            await dispatch('updateMomentStats', {
              momentStats: sceneStats?.momentRewards?.[momentId],
              projectSlug,
              momentId,
            });
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching collection data', error);
      })
      .finally(() => {});
    await dispatch('refreshProject', { projectSlug });
  },
  async refreshProject(
    { commit, dispatch, state },
    { projectSlug }: IGetProjectSceneMomentsRewardDetailsPayload,
  ) {
    commit('refreshProject', { projectSlug });
  },

  async initiateScenePage(
    { commit, dispatch, state },
    { projectSlug, sceneSlug }: IGetSceneAPIsPayload,
  ) {
    const prms: [Promise<Partial<IFilmSceneStatsOverview>>, Promise<IScene[]>] =
      [
        this.$filmApiService.getSceneStats(projectSlug, sceneSlug), // `${this.collectionsUrl}/${projectSlug}/scene/${sceneSlug}/stats`,
        this.$filmApiService.getScenes(projectSlug),
      ];

    await Promise.all(prms)
      .then(async (responses) => {
        const [sceneStats, scenes] = responses;

        await dispatch('updateSceneStats', {
          sceneStats: {
            totalRewards:
              (Number(sceneStats.totalRewards) || 0)?.toFixed?.(2) || 0,
            lastDayRewards:
              (Number(sceneStats.lastDayRewards) || 0)?.toFixed?.(2) || 0,
            distroBoost: sceneStats.distroBoost,
          },
          projectSlug,
          sceneSlug,
        });

        for (const momentId of Object.keys(sceneStats?.momentRewards ?? {})) {
          await dispatch('updateMomentStats', {
            momentStats: sceneStats?.momentRewards?.[momentId],
            projectSlug,
            momentId,
          });
        }
        await dispatch('updateScenes', { scenes, projectSlug });
      })
      .catch((error) => {
        console.error('Error fetching collection data', error);
      })
      .finally(() => {
        console.log('Scene page data fetched');
      });
    await dispatch('refreshProject', { projectSlug });
  },

  async initiateDashboard(
    { commit, dispatch, state },
    {}: IGetSceneAPIsPayload,
  ) {
    const prms: [
      Promise<IUserRewardsDistribution[]>,
      Promise<IUserPopcornBuckets[]>,
      Promise<{ totalRewards: number }>,
      Promise<{ lastDayRewards: number }>,
    ] = [
      this.$filmApiService.getUserRewardDistributionSimpleOverview(),
      this.$filmApiService.getUserPopcornBuckets(),
      this.$filmApiService.getUserTotalRewardsDistribution(),
      this.$filmApiService.getUserLastDayRewardsDistribution(),
    ];

    await Promise.all(prms).then(async (responses) => {
      const [
        userRewardsDistribution,
        userPopcornBuckets,
        totalRewards,
        lastDayRewards,
      ] = responses;

      await dispatch('updateUserRewardsDistribution', {
        rewards: userRewardsDistribution,
      });
      await dispatch('updateUserPopcornBuckets', {
        buckets: userPopcornBuckets,
      });
      await dispatch('updateUserTotalRewards', totalRewards);
      await dispatch('updateUserLastDayRewards', lastDayRewards);
    });
  },

  async updateUserRewardsDistribution(
    { commit, dispatch, state },
    payload: { rewards: IUserRewardsDistribution[] },
  ) {
    commit('updateUserRewardsDistribution', { rewards: payload.rewards });
  },

  async updateUserPopcornBuckets(
    { commit, dispatch, state },
    payload: { buckets: IUserPopcornBuckets[] },
  ) {
    commit('updateUserPopcornBuckets', { buckets: payload.buckets });
  },

  async updateUserTotalRewards(
    { commit, dispatch, state },
    payload: { totalRewards: number },
  ) {
    commit('updateUserTotalRewards', payload);
  },

  async updateUserLastDayRewards(
    { commit, dispatch, state },
    payload: { lastDayRewards: number },
  ) {
    commit('updateUserLastDayRewards', payload);
  },
};
